/* Under the letter: from inquiry to a finished proposal. The steps on the left (what happens, minute by minute);
   on the right the result – the customer's quote for MS Nordlys, rendered by the system's own QuoteView
   inside a browser frame you can scroll. It scrolls by itself slowly while in view; a wheel or a finger takes over. */
import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, Printer, ShieldCheck } from "lucide-react";
import { QuoteView } from "@/components/canvas/QuoteView";
import { useReveal } from "./proposalUtils";
import { CASE, QUOTE_TEXT_META, QUOTE_VM } from "./los/losMockData";

/* kapitlene i kundens dokument – samme ankre som QuoteView setter (data-section) */
export const NAV = [
  { id: "hode", label: "Tilbud" },
  { id: "omfang", label: "Leveransen" },
  { id: "priser", label: "Priser" },
  { id: "opsjoner", label: "Opsjoner" },
  { id: "forutsetninger-poster", label: "Forutsetninger" },
  { id: "vilkar", label: "Vilkår" },
];
const reduced = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* a touch screen (no hover, coarse pointer): the document only tours there – a swipe keeps scrolling the page */
const useTouch = () => {
  const [touch, setTouch] = useState(() => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(hover: none) and (pointer: coarse)");
    const onChange = (e) => setTouch(e.matches);
    setTouch(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return touch;
};

const Lines = ({ text, as: Tag = "h2", className, testid }) => {
  const parts = text.split("\n");
  return (
    <Tag className={className} data-testid={testid}>
      {parts.map((l, i) => (
        <span key={l} className="kvl-line">
          {l}
          {i < parts.length - 1 && <br />}
        </span>
      ))}
    </Tag>
  );
};

/* the document is laid out at its own width (980 px, or the phone's) and scaled down only when the column is narrower */
const geoFor = (w) => {
  if (w < 700) return { mode: "narrow", vw: Math.max(390, w), s: w / Math.max(390, w), w };
  const vw = w >= 1000 ? 1120 : 980;
  return { mode: "full", vw, s: Math.min(1, w / vw), w };
};

/* the phone: at most 360 px wide, centred, with a 10 px bezel; the screen is about twice as tall as it is wide */
const DEVICE_MAX = 360;
const BEZEL = 10;
const GUTTER = 22;
const phoneScreenHeight = (w) => {
  const dw = Math.min(Math.max(280, w - 2 * GUTTER), DEVICE_MAX);
  const sw = dw - 2 * BEZEL;
  return Math.round(Math.min(700, Math.max(540, sw * 2)));
};

/* slow tour through the document while in view; the reader takes over with wheel / touch / pointer, the tour resumes after a pause */
const useTour = (ref, active) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !active || reduced()) return undefined;
    let raf = 0;
    let userUntil = 0;
    let holdUntil = performance.now() + 2200;
    let last = performance.now();
    let pos = el.scrollTop;
    let returning = false;
    const speed = 42; // document px per second
    const onUser = () => {
      userUntil = performance.now() + 9000;
      returning = false;
    };
    el.addEventListener("wheel", onUser, { passive: true });
    el.addEventListener("touchstart", onUser, { passive: true });
    el.addEventListener("pointerdown", onUser);
    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const max = el.scrollHeight - el.clientHeight;
      if (now < userUntil) {
        pos = el.scrollTop;
        holdUntil = now + 800;
      } else if (returning) {
        if (el.scrollTop <= 2) {
          returning = false;
          pos = 0;
          holdUntil = now + 2200;
        }
      } else if (now >= holdUntil) {
        if (pos >= max - 1) {
          returning = true;
          el.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          pos = Math.min(max, pos + speed * dt);
          el.scrollTop = pos;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("wheel", onUser);
      el.removeEventListener("touchstart", onUser);
      el.removeEventListener("pointerdown", onUser);
    };
  }, [ref, active]);
};

/* which chapter is under the top bar – for the section pills */
const useActiveSection = (ref) => {
  const [id, setId] = useState(NAV[0].id);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const top = el.getBoundingClientRect().top + 120;
      let cur = NAV[0].id;
      NAV.forEach((n) => {
        const sec = el.querySelector(`[data-section="${n.id}"]`);
        if (sec && sec.getBoundingClientRect().top <= top) cur = n.id;
      });
      setId(cur);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [ref]);
  return id;
};

/* the customer's page: the system's own quote page – a slim toolbar (back · title · status · print · PDF) over the white paper,
   and the document itself rendered by the same QuoteView the workspace uses. On the phone the bar sits under a status strip. */
const StatusBar = () => (
  <div className="flex h-11 items-end justify-between px-7 pb-1 text-[13px] font-semibold tracking-[-0.01em]" aria-hidden="true">
    <span>9:41</span>
    <span className="flex items-center gap-1.5">
      <svg width="17" height="11" viewBox="0 0 17 11" fill="currentColor"><rect x="0" y="7" width="3" height="4" rx="0.8" /><rect x="4.5" y="5" width="3" height="6" rx="0.8" /><rect x="9" y="2.5" width="3" height="8.5" rx="0.8" /><rect x="13.5" y="0" width="3" height="11" rx="0.8" /></svg>
      <svg width="25" height="12" viewBox="0 0 25 12" fill="none"><rect x="0.5" y="0.5" width="21" height="11" rx="3" stroke="currentColor" opacity="0.4" /><rect x="2" y="2" width="16" height="8" rx="1.6" fill="currentColor" /><path d="M23 4v4a2 2 0 0 0 0-4Z" fill="currentColor" opacity="0.4" /></svg>
    </span>
  </div>
);

const CustomerPage = ({ nar, section, device }) => (
  <div className="mb kvl-doc-page min-h-full bg-[hsl(210_22%_95%)] text-foreground" data-testid="tilbod-showcase-page">
    <div className="sticky top-0 z-30 border-b border-black/[0.06] bg-white/90 backdrop-blur-xl">
      {device && <StatusBar />}
      <div className={`mx-auto flex h-12 max-w-[1120px] items-center gap-3 ${nar ? "px-3" : "px-5"}`}>
        <span className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] text-foreground/70"><ArrowLeft className="h-3.5 w-3.5" /> Saken</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5 text-[12.5px] text-muted-foreground">
          <span className="font-medium text-foreground">Tilbud {CASE.number} · rev. 1</span>
          <span className="inline-flex items-center gap-1 text-[hsl(var(--status-ok))]"><ShieldCheck className="h-3 w-3" /> Kontrollert</span>
          {!nar && (
            <nav className="ml-2 flex items-center gap-1 rounded-full bg-black/[0.045] p-[3px]" aria-label="Kapitler">
              {NAV.map((n) => (
                <span key={n.id} className={`h-7 rounded-full px-3 text-[12px] font-medium leading-7 transition-colors duration-300 ${section === n.id ? "bg-white text-foreground shadow-[0_1px_2px_hsl(var(--shadow-color)/0.1)]" : "text-foreground/55"}`} data-testid={`tilbod-showcase-nav-${n.id}`}>
                  {n.label}
                </span>
              ))}
            </nav>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[12.5px] font-medium">
          {!nar && <span className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-foreground/80"><Printer className="h-3.5 w-3.5" /> Skriv ut</span>}
          <span className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.1] bg-white px-2.5"><Download className="h-3.5 w-3.5" /> PDF</span>
        </div>
      </div>
    </div>
    <main className={`mx-auto max-w-[1120px] pb-16 ${nar ? "px-2 pt-4" : "px-6 pt-8"}`}>
      <div className="mx-auto max-w-[940px] rounded-[18px] bg-white pb-6 shadow-[0_1px_2px_hsl(212_40%_16%/0.06),0_24px_60px_-30px_hsl(212_40%_16%/0.35)]">
        <QuoteView vm={QUOTE_VM} wide={!nar} status="kontrollert" texts={QUOTE_TEXT_META} customerView />
      </div>
      <p className="mt-8 text-center text-[12px] text-muted-foreground">Delt av Egil Sønstabø, LOS Marine AS · kundevisning – kost, DB og interne notater finnes ikke her.</p>
    </main>
  </div>
);

const DocFrame = ({ url, caption, tour }) => {
  const outer = useRef(null);
  const scroller = useRef(null);
  const [geo, setGeo] = useState({ mode: "full", vw: 980, s: 0.8, w: 980 });
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = outer.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setGeo(geoFor(e.contentRect.width)));
    ro.observe(el);
    const io = new IntersectionObserver((entries) => setActive(entries[entries.length - 1].isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, []);
  useTour(scroller, active);
  const section = useActiveSection(scroller);
  const nar = geo.mode === "narrow";

  /* the phone: the customer's page inside a device, 1:1, scrolling by itself */
  if (nar) {
    const ih = phoneScreenHeight(geo.w);
    return (
      <div ref={outer} className="kvl-doc-wrap kvl-doc-wrap-device">
        <div className="kvl-device" data-mode="narrow" data-active={active ? "1" : "0"} data-testid="tilbod-showcase-doc">
          <div className="kvl-device-screen" style={{ height: ih }}>
            <span className="kvl-device-island" aria-hidden="true" />
            <div ref={scroller} className="kvl-doc-scroll" style={{ height: ih }} data-testid="tilbod-showcase-scroller" aria-label={caption} tabIndex={0}>
              <div className="kvl-doc-inert" aria-hidden="true">
                <CustomerPage nar section={section} device />
              </div>
            </div>
            <span className="kvl-device-home" aria-hidden="true" />
            {tour && (
              <span className="kvl-doc-tour" data-testid="tilbod-showcase-tour">
                {tour}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  const ih = 880;
  return (
    <div ref={outer} className="kvl-doc-wrap">
      <div className="kvl-frame">
        <div className="mb-frame kvl-doc-frame" data-mode={geo.mode} data-active={active ? "1" : "0"} data-testid="tilbod-showcase-doc">
          <div className="mb-chrome" aria-hidden="true">
            <span className="mb-dots"><i /><i /><i /></span>
            <span className="mb-url" data-testid="tilbod-showcase-url">{url}</span>
            <span className="mb-cap">{caption}</span>
          </div>
          <div className="mb-view kvl-doc-view" style={{ height: ih * geo.s }}>
            <div className="mb-scale" style={{ transform: `scale(${geo.s})`, width: geo.vw, height: ih }}>
              <div ref={scroller} className="kvl-doc-scroll" style={{ height: ih }} data-testid="tilbod-showcase-scroller" aria-label={caption} tabIndex={0}>
                <div className="kvl-doc-inert" aria-hidden="true">
                  <CustomerPage nar={false} section={section} />
                </div>
              </div>
            </div>
            {tour && (
              <span className="kvl-doc-tour" data-testid="tilbod-showcase-tour">
                {tour}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const QuoteShowcase = ({ d }) => {
  const c = d.showcase;
  const ref = useReveal();
  const touch = useTouch();
  if (!c) return null;
  const tour = touch && c.tourTouch ? c.tourTouch : c.tour;
  return (
    <section id="showcase" className="kvl-sec kvl-tone-dark kvl-showcase" data-head={c.eyebrow} data-tone="dark" data-testid="tilbod-showcase" ref={ref}>
      <div className="kvl-showcase-grid">
        <div className="kvl-showcase-copy kvl-reveal">
          <p className="kvl-showcase-eyebrow" data-testid="tilbod-showcase-eyebrow">
            {c.eyebrow}
          </p>
          <Lines text={c.title} className="kvl-h2 kvl-showcase-title" testid="tilbod-showcase-title" />
          {c.lede && (
            <p className="kvl-showcase-lede" data-testid="tilbod-showcase-lede">
              {c.lede}
            </p>
          )}
          <ol className="kvl-showcase-steps" data-testid="tilbod-showcase-steps">
            {c.steps.map((s, i) => (
              <li key={s.title} data-testid={`tilbod-showcase-step-${i + 1}`}>
                <span className="kvl-showcase-t">{s.t}</span>
                <span className="kvl-showcase-dot" aria-hidden="true" />
                <span className="kvl-showcase-name">{s.title}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="kvl-showcase-doc kvl-reveal kvl-reveal-2">
          <DocFrame url={c.url} caption={c.eyebrow} tour={tour} />
        </div>
      </div>
    </section>
  );
};
